import * as shlex from "shlex";
import type { McpServer, UpsertMcpServerInput } from "@/hooks/useMcpServers";
import type { McpProviderEntry } from "@/hooks/useMcpProviders";
import { MCP_DISPLAY_NAME_MAX_LENGTH } from "./constants";

export type McpFormState = {
  // Either editing an existing server (name set) or creating new (name = "").
  // null means dialog is closed.
  name: string | null;
  display_name: string;
  transport: "http" | "stdio";
  url: string;
  stdio_command: string;
  stdio_args_text: string;
  stdio_env: Array<{ key: string; value: string; sensitive: boolean }>;
  // How the user is editing a (custom) stdio server: the structured fields
  // ("form") or a pasted JSON blob ("json"). UI-only; both modes write through
  // to the stdio_command / stdio_args_text / stdio_env fields above so the
  // upsert payload is identical either way.
  stdio_config_mode: "form" | "json";
  // Editable buffer for the JSON editor. Kept in form state so the user's
  // formatting survives mode toggles; regenerated from the fields when the
  // user switches Form → JSON.
  stdio_json_text: string;
  provider_stdio_preset: {
    command: string;
    args_text: string;
    env: Array<{ key: string; value: string; sensitive: boolean }>;
  } | null;
  // True while the form's command line still matches provider_stdio_preset
  // (or was seeded from one). Does not disable the fields — provider branding
  // is derived via effectiveMcpProviderId / matchesStdioProviderPreset.
  stdio_command_locked: boolean;
  scope: "team" | "personal";
  provider_id: string | null;
  // The provider's canonical MCP URL from the catalog. Provider branding is
  // coupled to it: when an HTTP form's URL diverges from this value, the
  // provider link (and the provider logo derived from it) is dropped and the
  // server is treated as custom. null for custom servers and for forms where
  // the catalog URL is unknown (the backend enforces the same coupling).
  provider_url: string | null;
  // Links to setup-specific provider docs, surfaced as a small link in the
  // dialog when a known provider prefills the form. null for custom servers.
  oauth_docs_url: string | null;
  header_docs_url: string | null;
  stdio_docs_url: string | null;
  setup_guidance: string | null;
  setup_guidance_url: string | null;
  setup_guidance_url_label: string | null;
  setup_guidance_type: "oauth" | "header" | "stdio" | null;
  setup_callback_url: string | null;
  auth_type: "header" | "oauth" | "none";
  headers: Array<{ key: string; value: string }>;
};

export const EMPTY_FORM: McpFormState = {
  name: null,
  display_name: "",
  transport: "http",
  url: "",
  stdio_command: "",
  stdio_args_text: "",
  stdio_env: [],
  stdio_config_mode: "form",
  stdio_json_text: "",
  provider_stdio_preset: null,
  stdio_command_locked: false,
  // Default to "team" so the Add MCP Server dialog nudges users toward
  // configuring servers org-wide. Personal stays one toggle away.
  scope: "team",
  provider_id: null,
  provider_url: null,
  oauth_docs_url: null,
  header_docs_url: null,
  stdio_docs_url: null,
  setup_guidance: null,
  setup_guidance_url: null,
  setup_guidance_url_label: null,
  setup_guidance_type: null,
  setup_callback_url: null,
  // OAuth is the more common case (Sentry, Linear, Datadog, etc.) and matches
  // the default state shown in the Figma. Custom MCP servers can switch to
  // header auth via the segmented control.
  auth_type: "oauth",
  headers: [{ key: "Authorization", value: "Bearer " }],
};

export function formForNewServer(scope: "personal" | "team"): McpFormState {
  return { ...EMPTY_FORM, name: "", scope };
}

/**
 * A curated provider's stdio preset (pinned command line + env scaffold), or
 * null when the provider has no stdio setup.
 */
function providerStdioPreset(
  provider: McpProviderEntry | null | undefined,
): McpFormState["provider_stdio_preset"] {
  if (!provider?.stdio_command) return null;
  return {
    command: provider.stdio_command,
    args_text: formatStdioArgs(provider.stdio_args ?? []),
    env: envRows(provider.stdio_env ?? {}),
  };
}

export function formForProvider(
  provider: McpProviderEntry,
  scope: "personal" | "team",
): McpFormState {
  const stdioPreset = providerStdioPreset(provider);
  const isStdioDefault = provider.default_transport === "stdio";
  const authType = isStdioDefault
    ? "none"
    : provider.default_auth_type ?? provider.auth_type;

  return {
    name: "",
    display_name: provider.display_name,
    transport: isStdioDefault ? "stdio" : "http",
    url: isStdioDefault ? "" : provider.url,
    stdio_command: isStdioDefault ? stdioPreset?.command ?? "" : "",
    stdio_args_text: isStdioDefault ? stdioPreset?.args_text ?? "" : "",
    stdio_env: isStdioDefault ? stdioPreset?.env ?? [] : [],
    stdio_config_mode: "form",
    stdio_json_text: "",
    provider_stdio_preset: stdioPreset,
    stdio_command_locked: isStdioDefault && stdioPreset !== null,
    scope,
    provider_id: provider.slug,
    provider_url: provider.url || null,
    oauth_docs_url: provider.oauth_docs_url ?? null,
    header_docs_url: provider.header_docs_url ?? null,
    stdio_docs_url: provider.stdio_docs_url ?? null,
    setup_guidance: provider.setup_guidance ?? null,
    setup_guidance_url: provider.setup_guidance_url ?? null,
    setup_guidance_url_label: provider.setup_guidance_url_label ?? null,
    setup_guidance_type: provider.setup_guidance_type ?? null,
    setup_callback_url: provider.setup_callback_url ?? null,
    auth_type: authType,
    headers:
      authType === "header" ? [{ key: "Authorization", value: "Bearer " }] : [],
  };
}

export function formForExisting(
  name: string,
  server: McpServer,
  // Catalog entry matching server.provider_id, when the caller has one. A
  // provider-linked stdio server carries the catalog preset so its env
  // scaffold gets the same JSON-edit and paste treatment as a new provider
  // form; servers whose provider is missing from the catalog stay preset-less.
  provider?: McpProviderEntry | null,
): McpFormState {
  const authType =
    server.transport === "stdio"
      ? "none"
      : server.auth_type === "oauth"
      ? "oauth"
      : "header";

  return {
    name,
    display_name: server.display_name ?? name,
    transport: server.transport,
    url: server.url,
    stdio_command: server.stdio_command ?? "",
    stdio_args_text: formatStdioArgs(server.stdio_args ?? []),
    stdio_env: envRows(server.stdio_env ?? {}),
    stdio_config_mode: "form",
    stdio_json_text: "",
    provider_stdio_preset:
      server.transport === "stdio" && server.provider_id
        ? providerStdioPreset(provider)
        : null,
    stdio_command_locked:
      server.transport === "stdio" && Boolean(server.provider_id),
    scope: server.scope ?? "personal",
    provider_id: server.provider_id ?? null,
    // The server row doesn't carry the catalog URL; the backend drops the
    // provider link on save if the edited URL no longer matches it.
    provider_url: null,
    oauth_docs_url: server.oauth_docs_url ?? null,
    header_docs_url: server.header_docs_url ?? null,
    stdio_docs_url: server.stdio_docs_url ?? null,
    setup_guidance: provider?.setup_guidance ?? null,
    setup_guidance_url: provider?.setup_guidance_url ?? null,
    setup_guidance_url_label: provider?.setup_guidance_url_label ?? null,
    setup_guidance_type: provider?.setup_guidance_type ?? null,
    setup_callback_url: provider?.setup_callback_url ?? null,
    auth_type: authType,
    // Header servers seed rows from the stored header keys (values are never
    // returned by the API) so the user can enter replacement credentials.
    // Rows left value-less keep the stored credential for that key; rows the
    // user removes are dropped from the key set the upsert payload names,
    // which deletes the stored header.
    headers:
      authType === "header"
        ? (server.auth_header_keys.length > 0
            ? server.auth_header_keys
            : ["Authorization"]
          ).map((key) => ({
            key,
            value: key.toLowerCase() === "authorization" ? "Bearer " : "",
          }))
        : [],
  };
}

export function applyProviderStdioPreset(form: McpFormState): McpFormState {
  if (!form.provider_stdio_preset) {
    return {
      ...form,
      transport: "stdio",
      auth_type: "none",
      headers: [],
      stdio_command_locked: false,
    };
  }

  return {
    ...form,
    transport: "stdio",
    auth_type: "none",
    headers: [],
    stdio_command: form.provider_stdio_preset.command,
    stdio_args_text: form.provider_stdio_preset.args_text,
    stdio_env: form.provider_stdio_preset.env,
    stdio_command_locked: true,
  };
}

function envRows(
  env: Record<string, { value: string; sensitive: boolean }>,
): Array<{ key: string; value: string; sensitive: boolean }> {
  return Object.entries(env).map(([key, metadata]) => ({
    key,
    value: metadata.value,
    sensitive: metadata.sensitive,
  }));
}

// POSIX bin/sh style word splitting. Does not expand shell syntax.
export function tokenizeStdioArgs(input: string): string[] {
  return shlex.split(input);
}

export function formatStdioArgs(args: string[]): string {
  return shlex.join(args);
}

/**
 * Mirror backend upsert validation for stdio arguments: at least one argument,
 * and no entry that is blank after trimming (e.g. an empty or whitespace-only
 * quoted token like `""` or `"  "`), which the API rejects with a 422. Used by
 * both submit gating and the JSON editor footer so they never diverge.
 */
export function hasRequiredStdioArgs(args: string[]): boolean {
  return args.length > 0 && args.every((arg) => arg.trim() !== "");
}

export type ParsedStdioConfig = {
  /** Server key from an `mcpServers` blob; null when a bare object was pasted. */
  name: string | null;
  command: string;
  args: string[];
  env: Array<{ key: string; value: string; sensitive: boolean }>;
  /** Number of servers found in the blob (the editor only uses the first). */
  serverCount: number;
};

export type StdioConfigParseResult =
  | { ok: true; config: ParsedStdioConfig }
  | { ok: false; error: string };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Parse a pasted stdio MCP config. Accepts the shape users copy from docs —
 * `{ "mcpServers": { "<name>": { command, args?, env? } } }` — as well as a
 * bare server object (`{ "command": ... }`) or a raw name→server map. Only the
 * first server with a string `command` is used; env values become non-sensitive
 * rows (the JSON format has no sensitivity flag).
 */
export function parseStdioConfigJson(text: string): StdioConfigParseResult {
  if (!text.trim()) {
    return { ok: false, error: "Paste a JSON configuration to continue" };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : "Invalid JSON",
    };
  }

  if (!isPlainObject(parsed)) {
    return { ok: false, error: "Expected a JSON object" };
  }

  let serverMap: Record<string, unknown>;
  let bare = false;
  if (isPlainObject(parsed.mcpServers)) {
    serverMap = parsed.mcpServers;
  } else if (typeof parsed.command === "string") {
    serverMap = { server: parsed };
    bare = true;
  } else {
    serverMap = parsed;
  }

  // Use the first server with a usable (non-blank) command. Skipping
  // empty-command entries means a valid server defined after a placeholder or
  // typo entry in the same blob is still picked up rather than rejected.
  const entries = Object.entries(serverMap).filter(
    ([, v]) =>
      isPlainObject(v) &&
      typeof v.command === "string" &&
      v.command.trim() !== "",
  ) as Array<[string, Record<string, unknown>]>;

  if (entries.length === 0) {
    return { ok: false, error: 'No server with a "command" field found' };
  }

  const [key, server] = entries[0];
  const command = String(server.command).trim();

  if (server.args !== undefined && !Array.isArray(server.args)) {
    return { ok: false, error: '"args" must be an array of strings' };
  }
  const args = Array.isArray(server.args) ? server.args.map(String) : [];

  if (server.env !== undefined && !isPlainObject(server.env)) {
    return { ok: false, error: '"env" must be an object of string values' };
  }
  const env = isPlainObject(server.env)
    ? Object.entries(server.env).map(([envKey, value]) => ({
        key: envKey,
        value: String(value ?? ""),
        sensitive: false,
      }))
    : [];

  return {
    ok: true,
    config: {
      name: bare ? null : key,
      command,
      args,
      env,
      serverCount: entries.length,
    },
  };
}

/**
 * Serialize the structured stdio fields into the `mcpServers` blob shown in the
 * JSON editor. Used when the user switches Form → JSON so the editor reflects
 * what they've already entered.
 */
export function buildStdioConfigJson(input: {
  name?: string;
  command: string;
  argsText: string;
  env: Array<{ key: string; value: string; sensitive: boolean }>;
}): string {
  const key = (input.name ?? "").trim() || "server";

  let args: string[] = [];
  if (input.argsText.trim()) {
    try {
      args = tokenizeStdioArgs(input.argsText);
    } catch {
      args = input.argsText.trim().split(/\s+/);
    }
  }

  const env: Record<string, string> = {};
  for (const row of input.env) {
    const envKey = row.key.trim();
    if (!envKey) continue;
    env[envKey] = row.value;
  }

  const server: Record<string, unknown> = { command: input.command };
  if (args.length > 0) server.args = args;
  if (Object.keys(env).length > 0) server.env = env;

  return JSON.stringify({ mcpServers: { [key]: server } }, null, 2);
}

/**
 * Serialize a parsed stdio server as the canonical editor shape. Pasted MCP
 * docs often wrap configs as `mcpServers.<name>`, but Blacksmith persists the
 * form display name separately and generates its own internal key. Showing the
 * bare server avoids implying that the imported JSON key remains meaningful.
 */
export function buildBareStdioConfigJson(input: {
  command: string;
  args: string[];
  env: Array<{ key: string; value: string; sensitive: boolean }>;
}): string {
  const env: Record<string, string> = {};
  for (const row of input.env) {
    const envKey = row.key.trim();
    if (!envKey) continue;
    env[envKey] = row.value;
  }

  const server: Record<string, unknown> = { command: input.command };
  if (input.args.length > 0) server.args = input.args;
  if (Object.keys(env).length > 0) server.env = env;

  return JSON.stringify(server, null, 2);
}

/**
 * Compute the form state when a custom stdio server switches from Form mode
 * into JSON mode. Seeds the editor from the structured fields, then reparses
 * that blob and writes the normalized `stdio_args_text` back so the structured
 * field (read by Save gating and `formToUpsertInput`) agrees with what the
 * editor shows.
 *
 * Without the write-back, args the form couldn't tokenize (e.g. an unmatched
 * quote) would linger in `stdio_args_text` after the switch: the JSON footer
 * would validate, but Save would stay disabled via `hasValidStdioArgs` and a
 * forced upsert would no-op because `formToUpsertInput` re-tokenizes that
 * field. `stdio_env` is left as entered so per-row sensitivity flags survive
 * the switch.
 */
export function enterStdioJsonMode(form: McpFormState): McpFormState {
  const stdio_json_text = buildStdioConfigJson({
    name: form.display_name,
    command: form.stdio_command,
    argsText: form.stdio_args_text,
    env: form.stdio_env,
  });

  const parsed = parseStdioConfigJson(stdio_json_text);
  if (!parsed.ok) {
    // Blob is incomplete (e.g. no command yet); nothing to normalize.
    return { ...form, stdio_config_mode: "json", stdio_json_text };
  }

  return {
    ...form,
    stdio_config_mode: "json",
    stdio_json_text,
    stdio_args_text: formatStdioArgs(parsed.config.args),
  };
}

/**
 * Reconcile env rows parsed from a JSON blob with the rows already in form
 * state. The `mcpServers` JSON format has no sensitivity flag, so
 * `parseStdioConfigJson` returns every row as `sensitive: false`. Writing those
 * rows straight into form state would silently clear the sensitive flag a user
 * (or an existing server record) had set on a key, causing the upsert to store
 * a secret as non-sensitive. Carry the prior `sensitive` value forward by key;
 * keys that didn't exist before keep the parsed default (non-sensitive).
 */
export function mergeStdioEnvSensitivity(
  parsedEnv: McpFormState["stdio_env"],
  priorEnv: McpFormState["stdio_env"],
): McpFormState["stdio_env"] {
  const priorSensitiveByKey = new Map<string, boolean>();
  for (const row of priorEnv) {
    priorSensitiveByKey.set(row.key, row.sensitive);
  }
  return parsedEnv.map((row) => ({
    ...row,
    sensitive: priorSensitiveByKey.get(row.key) ?? row.sensitive,
  }));
}

/**
 * Overlay env values onto existing rows (e.g. a curated provider's preset
 * rows). Existing rows keep their order and sensitivity flags (the pasted or
 * prefilled format has no reliable flag), non-empty incoming values override,
 * keys missing from the incoming set keep their current values, and
 * incoming-only keys are appended.
 */
export function mergeEnvRowValues(
  baseEnv: McpFormState["stdio_env"],
  incomingEnv: McpFormState["stdio_env"],
): McpFormState["stdio_env"] {
  const incomingByKey = new Map(incomingEnv.map((row) => [row.key, row]));
  const merged = baseEnv.map((row) => {
    const override = incomingByKey.get(row.key);
    if (!override) return row;
    incomingByKey.delete(row.key);
    return { ...row, value: override.value || row.value };
  });
  return [...merged, ...Array.from(incomingByKey.values())];
}

/**
 * Env sync for JSON edits (live typing and the JSON -> Form switch) and
 * pastes on a stdio form. The parsed blob is the editing source of truth, but
 * a curated preset's env rows are the credential scaffolding the form is
 * built around, so scaffold rows the blob omits keep their current values
 * instead of being dropped. This holds even while the command line diverges
 * from the preset, so credentials entered on the scaffold survive a
 * divergence-and-back edit. Blob-only keys follow the blob exactly (present
 * rows are appended, removed rows go away), so transient keystroke states
 * don't accumulate rows. Forms without a preset (custom servers, or a
 * provider-linked server whose provider is missing from the catalog) have no
 * scaffold and take the blob's rows as-is.
 */
export function syncStdioEnvFromJson(
  form: McpFormState,
  parsedEnv: McpFormState["stdio_env"],
): McpFormState["stdio_env"] {
  const currentByKey = new Map(form.stdio_env.map((row) => [row.key, row]));
  const scaffold = (form.provider_stdio_preset?.env ?? []).map(
    (row) => currentByKey.get(row.key) ?? row,
  );
  return mergeEnvRowValues(
    scaffold,
    mergeStdioEnvSensitivity(parsedEnv, form.stdio_env),
  );
}

/**
 * Apply a stdio MCP config blob pasted anywhere in the form. When the blob's
 * command line matches a curated catalog preset, only env values are taken
 * (credential paste onto the scaffold). Otherwise the blob supplies the full
 * command/args/env and, when the Name field is still blank, the blob's
 * server key — matching HTTP's "edit the identity → become custom" behavior.
 */
export function applyStdioConfigPaste(
  form: McpFormState,
  config: ParsedStdioConfig,
): McpFormState {
  const preset = form.provider_stdio_preset;
  const matchesPreset =
    preset !== null &&
    matchesPinnedCommandLine(
      { command: config.command, args: config.args },
      {
        command: preset.command,
        args: tokenizePinnedArgs(preset.args_text),
      },
    );

  if (matchesPreset) {
    // A form not yet on stdio (e.g. a provider defaulting to OAuth) first
    // moves onto its catalog preset, exactly as if the user had chosen
    // Command (Stdio) in the Setup dropdown; the blob then only contributes
    // env values on top of that base. Command/args are pinned back to the
    // preset so a form whose command line was edited away re-couples to the
    // catalog when the pasted blob matches it; env rows are kept, not reset.
    const moved =
      form.transport === "stdio" ? form : applyProviderStdioPreset(form);
    const base = {
      ...moved,
      stdio_command: preset.command,
      stdio_args_text: preset.args_text,
    };
    const env = mergeEnvRowValues(base.stdio_env, config.env);
    return {
      ...base,
      auth_type: "none",
      headers: [],
      stdio_env: env,
      stdio_json_text: buildBareStdioConfigJson({
        command: base.stdio_command,
        args: tokenizePinnedArgs(base.stdio_args_text),
        env,
      }),
      stdio_command_locked: true,
    };
  }

  // A divergent paste unbrands the server but keeps the preset's env
  // scaffold, so credential rows the blob omits are not wiped.
  const env = syncStdioEnvFromJson(form, config.env);
  return {
    ...form,
    transport: "stdio",
    auth_type: "none",
    headers: [],
    stdio_command: config.command,
    stdio_args_text: formatStdioArgs(config.args),
    stdio_env: env,
    stdio_json_text: buildBareStdioConfigJson({
      command: config.command,
      args: config.args,
      env,
    }),
    stdio_command_locked: false,
    // Cap the JSON-derived name to the same limit the Name input enforces so
    // a long mcpServers key can't push the upsert past backend validation.
    ...(config.name && !form.display_name.trim()
      ? { display_name: config.name.slice(0, MCP_DISPLAY_NAME_MAX_LENGTH) }
      : {}),
  };
}

/**
 * Tokenize a pinned args string, falling back to whitespace splitting when
 * shlex can't parse it. Pinned args come from the provider catalog (or a
 * stored server record), so the fallback is defensive rather than expected.
 */
function tokenizePinnedArgs(argsText: string): string[] {
  try {
    return tokenizeStdioArgs(argsText);
  } catch {
    return argsText.trim().split(/\s+/).filter(Boolean);
  }
}

/**
 * Whether two command lines are identical (command + args, order-sensitive).
 */
export function matchesPinnedCommandLine(
  config: { command: string; args: string[] },
  pinned: { command: string; args: string[] },
): boolean {
  return (
    config.command === pinned.command &&
    config.args.length === pinned.args.length &&
    config.args.every((arg, i) => arg === pinned.args[i])
  );
}

export function collectEnv(
  env: McpFormState["stdio_env"],
): Record<string, { value: string; sensitive: boolean }> {
  const out: Record<string, { value: string; sensitive: boolean }> = {};
  for (const item of env) {
    const key = item.key.trim();
    const value = item.value.trim();
    if (!key || !value) continue;
    out[key] = { value, sensitive: item.sensitive };
  }
  return out;
}

/**
 * Non-blank env rows as the flat value map the stdio test endpoint expects.
 * The stdio analog of `collectHeaders`.
 */
export function collectEnvValues(
  env: McpFormState["stdio_env"],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const item of env) {
    const key = item.key.trim();
    const value = item.value.trim();
    if (!key || !value) continue;
    out[key] = value;
  }
  return out;
}

/**
 * Edit-mode variant of `collectEnvValues`, mirroring `collectEditHeaders`:
 * the returned map names the full key set currently in the editor so the
 * backend can fall back to the stored env value for blank rows and treat
 * stored keys missing from the map as removed.
 */
export function collectEditEnvValues(
  env: McpFormState["stdio_env"],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const item of env) {
    const key = item.key.trim();
    if (!key) continue;
    out[key] = item.value.trim();
  }
  return out;
}

const ENV_KEY_PARTIAL_PATTERN = /^$|^[A-Z_][A-Z0-9_]*$/;

export function normalizeEnvKeyTypedInput(value: string): string | null {
  const normalized = value.toUpperCase();
  return ENV_KEY_PARTIAL_PATTERN.test(normalized) ? normalized : null;
}

export function normalizeEnvKeyPastedInput(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[\s.-]+/g, "_")
    .replace(/[^A-Z0-9_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^[0-9]+/, "")
    .replace(/_+$/, "");
}

/** Auth-scheme prefixes that are not valid credentials on their own. */
const BARE_AUTH_SCHEMES = new Set(["bearer", "basic", "token"]);

/**
 * Reduce `[{key, value}]` header rows into the `Record<string, string>` the
 * API expects. Drops blank rows and rejects bare auth-scheme prefixes
 * (e.g. "Bearer" with no token) on the Authorization header.
 */
/** Case-insensitive display name key; mirrors backend `mb_strtolower`. */
export function normalizeMcpDisplayNameKey(displayName: string): string {
  return displayName.trim().toLocaleLowerCase();
}

/**
 * Returns a user-facing error when the form's display name collides with an
 * existing server in the same scope. Matches backend upsert validation:
 * team names are unique per org; personal names are unique per owner.
 * Cross-scope duplicates (team + personal sharing a name) are allowed.
 */
export function getDisplayNameCollisionError(
  form: McpFormState,
  servers: Record<string, McpServer>,
  currentUserId?: number,
): string | null {
  if (form.name === null) return null;

  const targetKey = normalizeMcpDisplayNameKey(form.display_name);
  if (!targetKey) return null;

  // Keep-name edits are always allowed, even when legacy rows from before
  // per-scope uniqueness was enforced share this display name: the edit
  // doesn't create a new collision, and blocking it would strand those
  // rows. Duplicates only surface when one of them is actually renamed.
  // Scope changes don't qualify: moving a server between team and personal
  // can create a brand-new collision in the destination scope.
  if (form.name) {
    const original = servers[form.name];
    if (
      original &&
      original.scope === form.scope &&
      normalizeMcpDisplayNameKey(original.display_name ?? form.name) ===
        targetKey
    ) {
      return null;
    }
  }

  const personalOwnerId =
    form.scope === "personal"
      ? resolvePersonalOwnerId(form, servers, currentUserId)
      : undefined;

  for (const [serverName, server] of Object.entries(servers)) {
    if (form.name !== "" && serverName === form.name) continue;
    if (server.scope !== form.scope) continue;

    const existingKey = normalizeMcpDisplayNameKey(
      server.display_name ?? serverName,
    );
    if (existingKey !== targetKey) continue;

    if (form.scope === "team") {
      return "A team MCP server with this name already exists";
    }

    const serverOwnerId = server.created_by?.id;
    if (personalOwnerId === undefined || serverOwnerId === undefined) {
      continue;
    }
    if (serverOwnerId === personalOwnerId) {
      return "A personal MCP server with this name already exists";
    }
  }

  return null;
}

function resolvePersonalOwnerId(
  form: McpFormState,
  servers: Record<string, McpServer>,
  currentUserId?: number,
): number | undefined {
  if (form.name) {
    return servers[form.name]?.created_by?.id;
  }
  return currentUserId;
}

export function collectHeaders(
  headers: McpFormState["headers"],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const h of headers) {
    const key = h.key.trim();
    const value = h.value.trim();
    if (!key || !value) continue;
    if (
      key.toLowerCase() === "authorization" &&
      BARE_AUTH_SCHEMES.has(value.toLowerCase())
    )
      continue;
    out[key] = value;
  }
  return out;
}

/** Trailing-slash-insensitive comparison key for provider URL pinning. */
function normalizeUrlForProviderMatch(url: string): string {
  return url.trim().replace(/\/+$/, "");
}

/**
 * Whether a stdio form's command line still matches its curated catalog
 * preset. Used for provider branding (same role as URL matching for HTTP)
 * and for env-scaffold paste/JSON sync.
 */
export function matchesStdioProviderPreset(form: McpFormState): boolean {
  const preset = form.provider_stdio_preset;
  if (!preset) return false;
  let args: string[];
  try {
    args = tokenizeStdioArgs(form.stdio_args_text);
  } catch {
    return false;
  }
  // The backend trims the submitted command before its own catalog match, so
  // stray whitespace alone must not unbrand the server.
  return matchesPinnedCommandLine(
    { command: form.stdio_command.trim(), args },
    { command: preset.command, args: tokenizePinnedArgs(preset.args_text) },
  );
}

/**
 * The provider id the form still qualifies for. Provider branding is coupled
 * to a catalog identity: for HTTP, the canonical URL; for stdio, the catalog
 * command line. Editing either away makes the form a custom server, so the
 * provider link (and the provider logo derived from it) is dropped. Forms
 * without a known catalog identity keep their link; the backend enforces the
 * same coupling on save.
 */
export function effectiveMcpProviderId(form: McpFormState): string | null {
  if (!form.provider_id) return null;
  if (form.transport === "http") {
    if (!form.provider_url) return form.provider_id;
    return normalizeUrlForProviderMatch(form.url) ===
      normalizeUrlForProviderMatch(form.provider_url)
      ? form.provider_id
      : null;
  }
  if (form.transport === "stdio") {
    if (!form.provider_stdio_preset) return form.provider_id;
    return matchesStdioProviderPreset(form) ? form.provider_id : null;
  }
  return form.provider_id;
}

/**
 * Edit-mode variant of collectHeaders: the returned map names the full key
 * set currently in the editor, so the API can treat stored keys missing from
 * it as removed. Keys whose value is blank map to "", meaning "keep the
 * stored credential". A bare auth scheme (e.g. the seeded "Bearer "
 * placeholder) is normalized to "" for every key, not just Authorization:
 * it is never a real credential, and the seeded placeholder must not turn
 * into a stored token when the user renames the row. The API keeps the
 * stored credential for blank keys it knows and drops blank keys it doesn't.
 */
export function collectEditHeaders(
  headers: McpFormState["headers"],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const h of headers) {
    const key = h.key.trim();
    if (!key) continue;
    const value = h.value.trim();
    out[key] = BARE_AUTH_SCHEMES.has(value.toLowerCase()) ? "" : value;
  }
  return out;
}

/**
 * Convert form state into the API upsert payload. Returns null when the form
 * is incomplete or closed (caller should treat that as a no-op).
 */
export function formToUpsertInput(
  form: McpFormState,
  servers: Record<string, McpServer>,
): UpsertMcpServerInput | null {
  if (!form.display_name || form.name === null) return null;
  if (form.transport === "http" && !form.url) return null;
  if (form.transport === "stdio" && !form.stdio_command) return null;

  let stdioArgs: string[] = [];
  if (form.transport === "stdio") {
    try {
      stdioArgs = tokenizeStdioArgs(form.stdio_args_text);
    } catch {
      return null;
    }
    if (!hasRequiredStdioArgs(stdioArgs)) return null;
  }

  const isEditingExisting = form.name !== "";
  const currentEnabled = isEditingExisting
    ? servers[form.name]?.enabled ?? true
    : true;

  // Creating a header server sends the collected non-blank headers (the API
  // requires a non-empty set). Editing one sends the full key set from the
  // editor: non-empty values replace the stored credential per key, blank
  // values keep it, and stored keys missing from the map are removed. The
  // field is omitted entirely when the key set matches storage, no
  // replacement value was entered, and the server is healthy, which keeps
  // the stored credentials without re-running the verification handshake.
  let headerPayload: Record<string, string> | null = null;
  if (form.auth_type === "header") {
    if (!isEditingExisting) {
      headerPayload = collectHeaders(form.headers);
    } else {
      const editHeaders = collectEditHeaders(form.headers);
      const storedKeys = servers[form.name]?.auth_header_keys ?? [];
      const sameKeySet =
        Object.keys(editHeaders).length === storedKeys.length &&
        storedKeys.every((key) => key in editHeaders);
      const hasReplacementValue = Object.values(editHeaders).some(
        (value) => value !== "",
      );
      // An errored server must be re-verified on save even when the user
      // kept every stored credential (Update credentials → Test → Save with
      // blank rows): naming the key set makes the backend re-run the
      // handshake so a fixed remote clears the error status.
      const needsReverify = servers[form.name]?.auth_status === "error";
      // An empty key set can't mean "delete every header" (a header server
      // requires at least one), so it is treated as "no credential change".
      // Omitting the field keeps Save consistent with Test, which also falls
      // back to the stored credentials for an empty map.
      if (
        Object.keys(editHeaders).length > 0 &&
        (!sameKeySet || hasReplacementValue || needsReverify)
      ) {
        headerPayload = editHeaders;
      }
    }
  }
  const stdioEnv = collectEnv(form.stdio_env);
  const providerId = effectiveMcpProviderId(form);

  return {
    display_name: form.display_name,
    transport: form.transport,
    url: form.transport === "http" ? form.url : "",
    scope: form.scope,
    enabled: currentEnabled,
    ...(isEditingExisting ? { name: form.name } : {}),
    // An explicit null clears a stored provider link when the URL was edited
    // away from the provider's canonical URL (omitting the key would keep it).
    ...(providerId
      ? { provider_id: providerId }
      : isEditingExisting && form.provider_id
      ? { provider_id: null }
      : {}),
    // auth_type is only set on create — once chosen, it can't change.
    ...(isEditingExisting ? {} : { auth_type: form.auth_type }),
    ...(form.transport === "stdio"
      ? {
          stdio_command: form.stdio_command,
          stdio_args: stdioArgs,
          stdio_env: stdioEnv,
        }
      : {}),
    ...(headerPayload !== null ? { auth_headers: headerPayload } : {}),
  };
}
